import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, query, where, getDocs, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import { SHUTDOWN_MODE } from '../shutdownConfig';
import './Navigation.css';

const Navigation = () => {
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const profileMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        return;
      }

      // Use role directly from currentUser since it's now merged with Firestore data
      setIsAdmin(currentUser.role === 'admin');
    };

    checkAdminRole();
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking the menu button
      if (menuButtonRef.current && menuButtonRef.current.contains(event.target)) {
        return;
      }
      
      // Close mobile menu if clicking outside
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
      
      // Close profile menu if clicking outside
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setUnreadCount(0);
      return;
    }

    // Subscribe to unread messages
    const messagesQuery = query(
      collection(db, 'webMessages'),
      where('ownerId', '==', currentUser.uid),
      where('readByOwner', '==', false)
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      setUnreadCount(snapshot.size);
    }, (error) => {
      console.error('Error fetching unread messages:', error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleProfileClick = () => {
    if (isMobile) {
      setShowMobileMenu(false);
      navigate('/profile');
    }
  };

  if (loading) {
    return <div className="navigation-loading">Loading...</div>;
  }

  if (SHUTDOWN_MODE) {
    return (
      <nav className="navigation">
        <div className="nav-brand">
          <Link to="/">
            <img src="/logo192.png" alt="ScanTag Logo" className="nav-logo" />
            <span>ScanTag</span>
          </Link>
        </div>
        <div className="nav-links">
          <Link to="/privacy" className="nav-link" onClick={() => setShowMobileMenu(false)}>Privacy</Link>
          <Link to="/legal" className="nav-link" onClick={() => setShowMobileMenu(false)}>Legal</Link>
          {isAdmin && (
            <Link to="/admin" className="nav-link admin-link" onClick={() => setShowMobileMenu(false)}>Admin</Link>
          )}
        </div>
      </nav>
    );
  }

  return (
    <nav className="navigation">
      <div className="nav-brand">
        <Link to="/">
          <img src="/logo192.png" alt="ScanTag Logo" className="nav-logo" />
          <span>ScanTag</span>
        </Link>
      </div>
      
      <button 
        ref={menuButtonRef}
        className="menu-toggle" 
        onClick={() => setShowMobileMenu(!showMobileMenu)}
        aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
      >
        {showMobileMenu ? '✕' : '☰'}
      </button>

      <div className={`nav-links ${showMobileMenu ? 'show' : ''}`} ref={mobileMenuRef}>
        {currentUser ? (
          <>
            <Link to="/buy" className="nav-link buy-link" onClick={() => setShowMobileMenu(false)}>Buy ScanTag</Link>
            <Link to="/items" className="nav-link" onClick={() => setShowMobileMenu(false)}>My Items</Link>
            <Link to="/inbox" className="nav-link messages-link" onClick={() => setShowMobileMenu(false)}>
              Messages
              {unreadCount > 0 && <span className="message-badge">{unreadCount}</span>}
            </Link>
            {isAdmin && (
              <Link to="/admin" className="nav-link admin-link" onClick={() => setShowMobileMenu(false)}>
                Admin
              </Link>
            )}
            <Link to="/profile" className="nav-link" onClick={() => setShowMobileMenu(false)}>
              Profile
            </Link>
          </>
        ) : (
          <>
            <Link to="/buy" className="nav-link buy-link" onClick={() => setShowMobileMenu(false)}>Buy ScanTag</Link>
            <Link to="/signin" className="nav-link" onClick={() => setShowMobileMenu(false)}>Sign In</Link>
            <Link to="/signup" className="nav-link signup-button" onClick={() => setShowMobileMenu(false)}>Sign Up</Link>
          </>
        )}
        <Link to="/" className="nav-link" onClick={() => setShowMobileMenu(false)}>Home</Link>
        <Link to="/about" className="nav-link" onClick={() => setShowMobileMenu(false)}>About</Link>
      </div>
    </nav>
  );
};

export default Navigation; 